import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * Reusable image uploader.
 *
 * Props:
 *   value       — current image URL/path (string) or null
 *   onChange(url)  — called with the new URL when upload completes, or null on remove
 *   label       — label shown above the box
 *   circular    — if true, renders as a circular avatar preview
 *   testid      — data-testid prefix
 */
export default function ImageUpload({
  value, onChange,
  label = "Image",
  circular = false,
  testid = "image-upload",
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const previewUrl = value
    ? (value.startsWith("http") ? value : `${backend}${value}`)
    : null;

  const openPicker = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      toast.error("Please choose a JPEG, PNG, WebP, or GIF image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB).");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = () => onChange(null);

  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-white/60 mb-2">{label}</div>
      <div className="flex items-center gap-4">
        {previewUrl ? (
          <div className={`relative shrink-0 ${circular ? "w-20 h-20 rounded-full" : "w-24 h-24 rounded"} overflow-hidden border border-adn-border bg-[#0b1428]`}>
            <img
              src={previewUrl}
              alt="preview"
              className="w-full h-full object-cover"
              data-testid={`${testid}-preview`}
            />
          </div>
        ) : (
          <div className={`shrink-0 ${circular ? "w-20 h-20 rounded-full" : "w-24 h-24 rounded"} border border-dashed border-adn-border bg-[#0b1428] flex items-center justify-center text-white/30`}>
            <Upload size={20} />
          </div>
        )}
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <button
            type="button"
            onClick={openPicker}
            disabled={busy}
            data-testid={`${testid}-btn`}
            className="inline-flex items-center gap-2 px-4 py-2 border border-primary/50 text-primary rounded text-sm hover:bg-primary/10 disabled:opacity-60 max-w-fit"
          >
            {busy ? <><Loader2 size={14} className="animate-spin"/> Uploading…</> :
                    <><Upload size={14}/> {value ? "Replace image" : "Upload image"}</>}
          </button>
          {value && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              data-testid={`${testid}-remove`}
              className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-red-400 max-w-fit"
            >
              <X size={12}/> Remove
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFile}
            className="hidden"
            data-testid={`${testid}-input`}
          />
          <div className="text-xs text-white/40">JPEG · PNG · WebP · GIF · max 5MB</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Convert an absolute or relative path/URL into an <img>-friendly src.
 * Handles both external URLs (Unsplash etc.) and our own `/api/files/...` paths.
 */
export function resolveImgSrc(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return `${backend}${url}`;
}
